import { useEffect, useRef, useCallback } from 'react';
import { SignalingService } from '@/services/signalingService';
import { useConnectionStore } from '@/store/connectionStore';
import { getWebSocketBaseUrl } from '@/lib/apiConfig';
import type { SignalingMessage, SignalingMessageType } from '@/types';

let signalingInstance: SignalingService | null = null;

/** Get or create the global signaling service instance for a specific room */
function getSignaling(roomId: string): SignalingService {
  if (signalingInstance && signalingInstance.roomId === roomId) {
    return signalingInstance;
  }

  // Disconnect previous room connection if changing rooms
  if (signalingInstance) {
    signalingInstance.disconnect();
    signalingInstance = null;
  }

  const baseUrl = getWebSocketBaseUrl();
  const url = `${baseUrl}/room/${roomId}/ws`;

  signalingInstance = new SignalingService({
    url,
    roomId,
    onOpen: () => useConnectionStore.getState().setWsState('connected'),
    onClose: () => useConnectionStore.getState().setWsState('disconnected'),
    onError: () => useConnectionStore.getState().setWsState('disconnected'),
  });

  return signalingInstance;
}

export function useSignaling(roomId: string | undefined) {
  const { setWsState } = useConnectionStore();
  const serviceRef = useRef<SignalingService | null>(null);

  useEffect(() => {
    if (!roomId) return;

    setWsState('connecting');
    const svc = getSignaling(roomId);
    serviceRef.current = svc;
    svc.connect();

    return () => {
      // Don't disconnect on simple re-renders; disconnect when leaving the room explicitly
    };
  }, [roomId, setWsState]);

  const send = useCallback((msg: Omit<SignalingMessage, 'timestamp'>) => {
    serviceRef.current?.send(msg);
  }, []);

  const on = useCallback(
    (type: SignalingMessageType, handler: (msg: SignalingMessage) => void) => {
      return serviceRef.current?.on(type, handler) ?? (() => {});
    },
    []
  );

  const disconnect = useCallback(() => {
    serviceRef.current?.disconnect();
    signalingInstance = null;
    setWsState('idle');
  }, [setWsState]);

  return { send, on, disconnect, service: serviceRef.current };
}
